import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { eur, frDate } from "@/lib/constants";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, Wallet, Send, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const EXPENSE_STATUS = {
  brouillon: ["Brouillon", "bg-zinc-500/15 text-zinc-300"],
  envoyee: ["À valider", "bg-[#FFD500]/15 text-[#E6B800]"],
  validee: ["Validée", "bg-blue-500/15 text-blue-400"],
  refusee: ["Refusée", "bg-red-500/15 text-red-400"],
  remboursee: ["Remboursée", "bg-emerald-500/15 text-emerald-400"],
};

export function StatusPill({ status }) {
  const [l, c] = EXPENSE_STATUS[status] || [status, ""];
  return <span data-testid={`expense-status-${status}`} className={`rounded-full px-2.5 py-1 text-xs font-medium ${c}`}>{l}</span>;
}

function FilePreview({ fileId }) {
  const [url, setUrl] = useState(null);
  const [mime, setMime] = useState("");
  useEffect(() => {
    if (!fileId) return undefined;
    let u;
    api.get(`/expense-files/${fileId}`, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setMime(data.type); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [fileId]);
  if (!fileId) return <p className="text-sm text-zinc-500">Aucun justificatif joint.</p>;
  if (!url) return <p className="text-sm text-zinc-500">Chargement du justificatif…</p>;
  return mime.startsWith("image/")
    ? <a href={url} target="_blank" rel="noreferrer"><img data-testid="expense-file-preview" src={url} alt="justificatif" className="max-h-72 w-full rounded-xl border border-zinc-800 object-contain" /></a>
    : <a data-testid="expense-file-preview" href={url} target="_blank" rel="noreferrer" className="text-sm text-[#FF6A00] underline">Ouvrir le PDF du justificatif</a>;
}

function AdminActions({ e, onDone }) {
  const [reason, setReason] = useState("");
  const act = async (action) => {
    try { await api.post(`/expenses/${e.id}/decision`, { action, reason }); toast.success("Note mise à jour, l'employé est prévenu"); onDone(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Erreur"); }
  };
  if (e.status === "envoyee") return (
    <div className="space-y-2">
      <Input data-testid="expense-reject-reason" placeholder="Motif (obligatoire en cas de refus)" value={reason} onChange={(ev) => setReason(ev.target.value)} className="border-zinc-800 bg-zinc-900" />
      <div className="flex gap-2">
        <Button data-testid="expense-approve" onClick={() => act("valider")} className="flex-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-500"><Check className="mr-1 h-4 w-4" />Valider</Button>
        <Button data-testid="expense-reject" onClick={() => act("refuser")} variant="outline" className="flex-1 rounded-full border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10"><X className="mr-1 h-4 w-4" />Refuser</Button>
      </div>
    </div>
  );
  if (e.status === "validee") return <Button data-testid="expense-reimburse" onClick={() => act("rembourser")} className="w-full rounded-full bg-[#FF6A00] text-white hover:bg-[#E65F00]"><Wallet className="mr-1 h-4 w-4" />Marquer comme remboursée</Button>;
  return null;
}

export default function ExpenseDetail({ e, isAdmin, meId, onClose, onChanged, onEdit }) {
  if (!e) return null;
  const mine = e.user_id === meId;
  const editable = (mine && ["brouillon", "refusee"].includes(e.status)) || isAdmin;
  const submit = async () => {
    try { await api.post(`/expenses/${e.id}/submit`); toast.success("Note envoyée"); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Erreur"); }
  };
  const del = async () => {
    if (!window.confirm("Supprimer définitivement cette note de frais et son justificatif ?")) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Note supprimée"); onClose(); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Erreur"); }
  };
  const row = (l, v) => <div className="flex justify-between gap-3 py-1.5 text-sm"><span className="text-zinc-500">{l}</span><span className="text-right text-white">{v || "—"}</span></div>;
  return (
    <Sheet open={!!e} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent data-testid="expense-detail" className="w-full overflow-y-auto border-zinc-800 bg-zinc-950 text-white sm:max-w-md">
        <SheetHeader><SheetTitle className="flex items-center gap-2 text-white">{e.vendor}<StatusPill status={e.status} /></SheetTitle></SheetHeader>
        <div className="mt-4 space-y-5">
          {e.status === "refusee" && e.reject_reason && <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">Motif du refus : {e.reject_reason}</p>}
          <div className="divide-y divide-zinc-800/60">
            {row("Employé", e.user_name)}{row("Événement", e.event_title || "Frais généraux")}{row("Date", e.date && frDate(e.date))}
            {row("N° TVA fournisseur", e.vat_number)}{row("HT", eur(e.amount_ht))}{row("TVA", eur(e.amount_tva))}
            {row("TTC", <strong className="text-[#FF6A00]">{eur(e.amount_ttc)}</strong>)}{row("Description", e.description)}
          </div>
          <FilePreview fileId={e.file_id} />
          {isAdmin && <AdminActions e={e} onDone={onChanged} />}
          <div className="flex flex-wrap gap-2">
            {(mine || isAdmin) && ["brouillon", "refusee"].includes(e.status) && <Button data-testid="expense-detail-submit" onClick={submit} className="rounded-full bg-[#FF6A00] text-white hover:bg-[#E65F00]"><Send className="mr-1 h-4 w-4" />Envoyer</Button>}
            {editable && <Button data-testid="expense-edit" onClick={() => onEdit(e)} variant="outline" className="rounded-full border-zinc-700 bg-transparent text-zinc-200"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>}
            {(isAdmin || (mine && e.status === "brouillon")) && <Button data-testid="expense-delete" onClick={del} variant="outline" className="rounded-full border-red-500/40 bg-transparent text-red-400"><Trash2 className="mr-1 h-4 w-4" />Supprimer</Button>}
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Historique</p>
            <ol data-testid="expense-history" className="space-y-2 border-l border-zinc-800 pl-4">
              {(e.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="text-sm"><span className="text-white">{h.action}</span> <span className="text-zinc-500">· {h.by} · {new Date(h.at).toLocaleString("fr-FR")}</span>{h.note && <p className="text-xs text-zinc-400">{h.note}</p>}</li>
              ))}
            </ol>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
